// Import existing code but fixing issues with types
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Plus, Edit, Trash2, Search } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuthStore } from "@/store/authStore";
import { MenuCategory, MenuCategoryCreate } from "@/types/menu";
import { useIsMobile } from "@/hooks/use-mobile";
import { useMenuStore } from "@/store/menuStore";
import { useMenuCategories, useMenuCacheUtils } from "@/services/menu/menuItem/fetchMenuItems";
import { useFilteredMenuItems } from "@/services/menu/menuItem/fetchMenuItems";

interface MenuCategoryTabsProps {
  activeCategory: string;
  setActiveCategory: (categoryId: string) => void;
  storeId: string;
  onStoreChange: (storeId: string) => void;
  stores: { id: string; name: string }[];
  categories?: MenuCategory[];
  isSearchActive?: boolean;
  isAddCategoryDialogOpen?: boolean;
  onAddCategoryDialogOpenChange?: (open: boolean) => void;
}

const categorySchema = z.object({
  name: z.string().min(2, {
    message: "Category name must be at least 2 characters.",
  }),
  description: z.string().optional(),
});

type CategorySchemaType = z.infer<typeof categorySchema>;

export function MenuCategoryTabs({
  activeCategory,
  setActiveCategory,
  storeId,
  onStoreChange,
  stores,
  categories: passedCategories,
  isSearchActive = false,
  isAddCategoryDialogOpen,
  onAddCategoryDialogOpenChange,
}: MenuCategoryTabsProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  
  // Use external dialog state if provided, otherwise use internal state
  const isDialogOpen = isAddCategoryDialogOpen !== undefined ? isAddCategoryDialogOpen : isAddDialogOpen;
  const setIsDialogOpen = onAddCategoryDialogOpenChange || setIsAddDialogOpen;
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState<MenuCategory | null>(
    null
  );
  const [deletingCategory, setDeletingCategory] = useState(false);
  const [selectedStore, setSelectedStore] = useState(storeId);
  const { user } = useAuthStore();
  const isMobile = useIsMobile();

  const {
    createCategory,
    updateCategory,
    deleteCategory,
  } = useMenuStore();

  const { data: fetchedCategories = [], isLoading, error } = useMenuCategories(selectedStore);
  const { invalidateCategories } = useMenuCacheUtils();

  // Use passed categories if available (for search results), otherwise use fetched categories
  const categories = passedCategories || fetchedCategories;

  const form = useForm<CategorySchemaType>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      name: "",
      description: "",
    },
  });

  useEffect(() => {
    setSelectedStore(storeId);
  }, [storeId]);

  // No need to fetch categories manually - TanStack Query handles this

  // Removed this useEffect to prevent infinite loop
  // The parent component (MenuManagement) already handles setting the initial active category

  useEffect(() => {
    if (error) {
      toast({
        title: t("error"),
        description: error.message,
        variant: "destructive",
      });
    }
  }, [error, toast, t]);

  const handleAddCategory = async (values: CategorySchemaType) => {
    try {
      if (!selectedStore) {
        toast({
          title: t("error"),
          description: t("noStoreSelected"),
          variant: "destructive",
        });
        return;
      }

      const newCategory: MenuCategoryCreate = {
        name: values.name,
        description: values.description,
        store_id: selectedStore,
      };

      await createCategory(newCategory);
      // Invalidate categories cache
      if (selectedStore) {
        invalidateCategories(selectedStore);
      }
      setIsDialogOpen(false);
      form.reset();
      toast({
        title: t("categoryCreated"),
        description: t("categoryCreatedDesc"),
      });
    } catch (error) {
      console.error("Error creating category:", error);
      toast({
        title: t("error"),
        description: t("errorCreatingCategory"),
        variant: "destructive",
      });
    }
  };

  const handleEditCategory = async (values: CategorySchemaType) => {
    try {
      if (!categoryToEdit) return;

      const updatedCategory = {
        name: values.name,
        description: values.description,
        store_id: selectedStore,
      };

      await updateCategory(categoryToEdit.id, updatedCategory);
      // Invalidate categories cache
      if (selectedStore) {
        invalidateCategories(selectedStore);
      }
      setIsEditDialogOpen(false);
      setCategoryToEdit(null);
      form.reset();
      toast({
        title: t("categoryUpdated"),
        description: t("categoryUpdatedDesc"),
      });
    } catch (error) {
      console.error("Error updating category:", error);
      toast({
        title: t("error"),
        description: t("errorUpdatingCategory"),
        variant: "destructive",
      });
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (!selectedStore) return;

    try {
      await deleteCategory(categoryId, selectedStore);
      // Invalidate categories cache
      if (selectedStore) {
        invalidateCategories(selectedStore);
      }
      if (categoryId === activeCategory) {
        setActiveCategory("all");
      }
      toast({
        title: t("categoryDeleted"),
        description: t("categoryDeletedDesc"),
      });
    } catch (error) {
      console.error("Error deleting category:", error);
      toast({
        title: t("error"),
        description: t("errorDeletingCategory"),
        variant: "destructive",
      });
    } finally {
      setDeletingCategory(false);
    }
  };

  return (
    <>
      {isLoading ? (
        <Skeleton className="h-10 md:h-12 w-full rounded-md mt-4" />
      ) : categories.length > 0 ? (
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 mt-4">
          {/* Search Indicator */}
          {isSearchActive && (
            <div className="mb-2 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Search className="h-3 w-3" />
                Showing categories with search results
              </span>
            </div>
          )}
          <Tabs
            value={activeCategory}
            onValueChange={(value) => {
              setActiveCategory(value);
            }}
          >
            <TabsList className="relative mb-2 flex w-max">
              {/* All Categories Tab - Always First */}
              <div className="flex items-center">
                <TabsTrigger
                  value="all"
                  className="text-xs md:text-sm py-1 px-2 md:py-2 md:px-3"
                >
                  {t("allCategories")}
                </TabsTrigger>
              </div>

              {/* Individual Category Tabs */}
              {categories.map((category) => (
                <div key={category.id} className="flex items-center">
                  <TabsTrigger
                    value={category.id}
                    className="text-xs md:text-sm py-1 px-2 md:py-2 md:px-3"
                  >
                    {category.name}
                  </TabsTrigger>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 md:h-8 md:w-8 p-0 ml-1"
                      >
                        <span className="sr-only">{t("openMenu")}</span>
                        <span className="text-lg leading-none">...</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => {
                          setCategoryToEdit(category);
                          form.setValue("name", category.name);
                          form.setValue(
                            "description",
                            category.description || ""
                          );
                          setIsEditDialogOpen(true);
                        }}
                      >
                        <Edit className="mr-2 h-4 w-4" />
                        {t("edit")}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          setDeletingCategory(true);
                          handleDeleteCategory(category.id);
                        }}
                        disabled={deletingCategory}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {t("delete")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </TabsList>
          </Tabs>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-4 md:p-8 text-center mt-4">
          <h3 className="mt-2 text-base md:text-lg font-semibold">
            {t("noCategoriesYet")}
          </h3>
          <p className="mb-4 mt-1 text-xs md:text-sm text-muted-foreground">
            {t("createFirstCategory")}
          </p>
        </div>
      )}

      {/* Add Category Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[425px] w-[95vw]">
          <DialogHeader>
            <DialogTitle>{t("addCategory")}</DialogTitle>
            <DialogDescription>{t("addCategoryDescription")}</DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleAddCategory)}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("categoryName")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("categoryNamePlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("categoryDescription")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("categoryDescriptionPlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit">{t("addCategory")}</Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px] w-[95vw]">
          <DialogHeader>
            <DialogTitle>{t("editCategory")}</DialogTitle>
            <DialogDescription>
              {t("editCategoryDescription")}
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleEditCategory)}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("categoryName")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("categoryNamePlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("categoryDescription")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("categoryDescriptionPlaceholder")}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit">{t("editCategory")}</Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
